import { formatChargedQty } from '@estimator/shared';
import { useState } from 'react';
import WoodFinishBreakdown from '../shared/WoodFinishBreakdown';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { BanquetteCostPreviewResult } from '@/utils/banquette-cost';

interface BanquetteCostPreviewProps {
  cost: BanquetteCostPreviewResult | null;
  lfQuantity: number;
  laborCost?: number;
  cncCost?: number;
  laborDetails?: Array<{
    rate_name: string;
    category: string;
    unit: string;
    units: number;
    cost: number;
    score_breakdown?: Array<{ label: string; minutes: number; cost: number; basis?: 'per_section' | 'total' }>;
  }>;
}

function fmt(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export default function BanquetteCostPreview({ cost, lfQuantity, laborCost, laborDetails, cncCost }: BanquetteCostPreviewProps) {
  const [expanded, setExpanded] = useState(false);

  if (!cost) return null;

  const hasDetails = cost.framing_materials.length > 0 || cost.slot_details.some((s) => s.cost > 0) ||
    cost.wood_finish_details.length > 0;

  return (
    <div className="rounded-lg" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border)' }}>
      {/* Compact summary bar */}
      <div
        className="flex items-center gap-6 px-4 py-2.5 cursor-pointer select-none"
        onClick={() => hasDetails && setExpanded(!expanded)}
        style={{ borderBottom: expanded ? '1px solid var(--border)' : 'none' }}
      >
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#e67e22' }} />
          <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#e67e22' }}>Cost</span>
        </div>

        <div className="flex items-center gap-6 flex-1">
          <div>
            <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Framing</span>
            <span className="text-xs font-semibold ml-1.5" style={{ color: 'var(--text-primary)' }}>{fmt(cost.framing_cost)}</span>
          </div>
          <div>
            <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Finish</span>
            <span className="text-xs font-semibold ml-1.5" style={{ color: 'var(--text-primary)' }}>{fmt(cost.finish_cost)}</span>
          </div>
          {/* Upholstery rows appear only when the base actually carries them,
              so a clad base looks exactly as it did before. */}
          {cost.fabric_cost > 0 && (
            <div>
              <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Fabric</span>
              <span className="text-xs font-semibold ml-1.5" style={{ color: 'var(--text-primary)' }}>{fmt(cost.fabric_cost)}</span>
            </div>
          )}
          {cost.foam_cost > 0 && (
            <div>
              <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Foam</span>
              <span className="text-xs font-semibold ml-1.5" style={{ color: 'var(--text-primary)' }}>{fmt(cost.foam_cost)}</span>
            </div>
          )}
          {cost.dacron_cost > 0 && (
            <div>
              <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Dacron</span>
              <span className="text-xs font-semibold ml-1.5" style={{ color: 'var(--text-primary)' }}>{fmt(cost.dacron_cost)}</span>
            </div>
          )}
          {cost.hardware_cost > 0 && (
            <div>
              <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Hardware</span>
              <span className="text-xs font-semibold ml-1.5" style={{ color: 'var(--text-primary)' }}>{fmt(cost.hardware_cost)}</span>
            </div>
          )}
          {cost.wood_finish_cost > 0 && (
            <div>
              <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Finishing</span>
              <span className="text-xs font-semibold ml-1.5" style={{ color: 'var(--text-primary)' }}>{fmt(cost.wood_finish_cost)}</span>
            </div>
          )}
          {laborCost != null && laborCost > 0 && (
          <div>
            <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Labor</span>
            <span className="text-xs font-semibold ml-1.5" style={{ color: 'var(--text-primary)' }}>{fmt(laborCost)}</span>
          </div>
          )}
          {cncCost != null && cncCost > 0 && (
            <div>
              <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>CNC</span>
              <span className="text-xs font-semibold ml-1.5" style={{ color: 'var(--text-primary)' }}>{fmt(cncCost)}</span>
            </div>
          )}
          <div>
            <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Total</span>
            <span className="text-sm font-bold ml-1.5" style={{ color: '#e67e22' }}>{fmt(cost.total_cost + (laborCost ?? 0) + (cncCost ?? 0))}</span>
          </div>
          {lfQuantity > 0 && (
            <div>
              <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Rate</span>
              {/*
                Computed here rather than read from cost.rate_per_lf, which is
                materials-only. The cushion previews already build their /LF the
                same way, so all component types now mean the same thing by it:
                everything in the Total, divided by LF.
              */}
              <span className="text-xs font-semibold ml-1.5" style={{ color: 'var(--text-secondary)' }}>{fmt((cost.total_cost + (laborCost ?? 0) + (cncCost ?? 0)) / lfQuantity)}/LF</span>
            </div>
          )}
          {cost.rib_count > 0 && (
            <div>
              <span className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Ribs</span>
              <span className="text-xs ml-1.5" style={{ color: 'var(--text-secondary)' }}>{cost.rib_count}</span>
            </div>
          )}
        </div>

        {hasDetails && (
          expanded
            ? <ChevronUp size={14} style={{ color: 'var(--text-muted)' }} />
            : <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />
        )}
      </div>

      {(cost.hardware_warnings ?? []).length > 0 && (
        <div
          className="px-4 py-1.5 text-[11px]"
          style={{ color: '#e67e22', borderTop: '1px solid var(--border)' }}
        >
          {(cost.hardware_warnings ?? []).map((w, i) => (
            <div key={i}>⚠ {w}</div>
          ))}
        </div>
      )}

      {/* Expandable detail breakdown */}
      {expanded && <WoodFinishBreakdown rows={cost.wood_finish_details} />}
      {expanded && (
        <div className="px-4 py-3 grid grid-cols-2 gap-4">
          {/* Framing breakdown */}
          {cost.framing_materials.length > 0 && (
            <div>
              <div className="text-[10px] font-medium mb-1.5 uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Framing Materials</div>
              {cost.framing_materials.map((m, i) => (
                <div key={i} className="flex justify-between text-xs mb-0.5">
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {m.material_name}{' '}
                    <span style={{ color: 'var(--text-muted)' }}>
                      {/* The quantity actually billed, in the material's own
                          unit. This printed square footage for every row alike,
                          so a board bought by the linear foot advertised an area
                          beside a linear-foot price. */}
                      ({m.charged_qty != null && m.charged_unit
                        ? formatChargedQty(m.charged_qty, m.charged_unit)
                        : `${m.total_sqft.toFixed(1)} sqft`}
                      {m.num_sheets && m.charged_unit !== 'sht' ? `, ${m.num_sheets} sht` : ''})
                    </span>
                  </span>
                  <span className="font-medium ml-3 flex-shrink-0" style={{ color: 'var(--text-primary)' }}>{fmt(m.cost)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Labor breakdown */}
          {laborDetails && laborDetails.length > 0 && (
            <div>
              <div className="text-[10px] font-medium mb-1.5 uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Labor</div>
              {laborDetails.map((d, i) => (
                <div key={i} className="mb-1">
                  <div className="flex justify-between text-xs">
                    <span style={{ color: 'var(--text-secondary)' }}>
                      {d.rate_name} <span style={{ color: 'var(--text-muted)' }}>({d.units} {d.unit})</span>
                    </span>
                    <span className="font-medium ml-3 flex-shrink-0" style={{ color: 'var(--text-primary)' }}>{fmt(d.cost)}</span>
                  </div>
                  {d.score_breakdown && d.score_breakdown.length > 0 && (
                    <div className="ml-3 mt-0.5 pl-2 border-l border-[var(--border)]">
                      {d.score_breakdown.map((row, j) => (
                        <div key={j} className="flex justify-between text-[10px]" style={{ color: 'var(--text-muted)' }}>
                          <span>{row.label} <span className="tabular-nums">({row.minutes} min)</span></span>
                          <span className="tabular-nums ml-2">{fmt(row.cost)}</span>
                        </div>
                      ))}
                      <div
                        className="flex justify-between text-[10px] font-semibold mt-0.5 pt-0.5 border-t border-[var(--border)]"
                        style={{ color: 'var(--text-secondary)' }}
                      >
                        <span>
                          Complexity Score{' '}
                          <span className="tabular-nums">
                            ({Math.round(d.score_breakdown.reduce((sum, r) => sum + r.minutes, 0))} min)
                          </span>
                        </span>
                        <span className="tabular-nums ml-2">{fmt(d.score_breakdown.reduce((sum, r) => sum + r.cost, 0))}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Slot breakdown */}
          {cost.slot_details.some((s) => s.cost > 0) && (
            <div>
              <div className="text-[10px] font-medium mb-1.5 uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Finish Slots</div>
              {cost.slot_details.filter((s) => s.cost > 0).map((s, i) => (
                <div key={i} className="flex justify-between text-xs mb-0.5">
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {s.name} <span style={{ color: 'var(--text-muted)' }}>({s.sqft.toFixed(1)} sqft)</span>
                  </span>
                  <span className="font-medium ml-3 flex-shrink-0" style={{ color: 'var(--text-primary)' }}>{fmt(s.cost)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
